import { Injectable } from '@angular/core';
import { formatCurrency, formatDate } from '@angular/common';
import { combineLatest, map, Observable, take } from 'rxjs';

//* Core
import {
  EmployeeProfileService,
  PaystubsCollectionService,
  PaystubDetailsService,
  TaxFormsNoticeService,
  ScreenSizeService,
} from '@core/services';

import {
  BenefitsDetail,
  Check,
  DetailRows,
  PaystubData,
  PaystubDetailItem,
  EmployeePackage,
  PaystubInfo,
  TaxForm,
} from '@core/models';

//* Shared
import { ImagePosition, TaxFormType, WidgetType } from '@shared/enums';
import { buildWidget, widgetFacts } from '@shared/utils';
import { ChartOptions, WidgetContent, WidgetDetail } from '@shared/models';

//* Base
import {
  caption,
  barchartOptions,
  icons,
  mobileTaxWidgets,
  piechartOptions,
} from '@dashboard/constants';
import { Caption, Dashboard, DashboardDetail } from '@dashboard/models';
import * as utils from '@dashboard/utils';

interface DashboardWidgets {
  dashboard: WidgetDetail[];
  taxOnly: WidgetDetail[];
}

@Injectable()
export class DashboardService {
  icons = caption.icons;
  iconIndex: number;
  private randomNumToThree = utils.randomNumToThree();
  showAlert = true;

  //#region //? Observables
  chartOptions$: Observable<ChartOptions>;
  dashboard$: Observable<Dashboard>;
  dashboardWidgets$: Observable<DashboardWidgets>;

  //! THIS NEVER CHANGES
  private paystubInfos$ = this.paystubsCollectionService.checks$.pipe(
    take(1),
    map(checks => this.buildPaystubInfos(checks))
  );

  private widgets$ = combineLatest([
    this.profileService.employeeProfile$,
    this.taxFormsNoticeService.taxFormsNotice$,
    this.paystubDetailsService.paystubDetailsData$,
  ]).pipe(
    map(([profile, taxFormsNotice, paystubData]) =>
      this.buildWidgetList(profile, taxFormsNotice, paystubData)
    )
  );
  //#endregion

  constructor(
    private paystubsCollectionService: PaystubsCollectionService,
    private paystubDetailsService: PaystubDetailsService,
    private profileService: EmployeeProfileService,
    private screenSizeService: ScreenSizeService,
    private taxFormsNoticeService: TaxFormsNoticeService
  ) {
    this.chartOptions$ = this.paystubDetailsService.paystubDetailsData$.pipe(
      map(paystubData => ({
        bar: this.buildBarChartOptions(paystubData),
        pie: this.buildPieChartOptions(paystubData),
      }))
    );

    this.dashboard$ = combineLatest([
      this.paystubDetailsService.paystubDetailsData$,
      this.paystubInfos$,
    ]).pipe(
      map(([paystubData, paystubInfos]) => ({
        details: this.buildDashboardDetail(paystubData),
        paystubInfos,
      })),
      map(({ details, paystubInfos }) => ({
        bannerMessage: this.buildBannerDetail(details),
        paystubInfos,
        details,
      }))
    );

    //* Widgets you receive vary between mobile or desktop views
    this.dashboardWidgets$ = combineLatest([
      this.widgets$,
      this.screenSizeService.isSmallScreens$,
    ]).pipe(
      map(([widgets, isMobile]) => ({
        dashboard: this.buildNoTaxWidgetsIfMobile(widgets, isMobile),
        taxOnly: this.buildTaxOnlyWidget(widgets),
      }))
    );
  }

  private buildBannerDetail(dashboardDetail: DashboardDetail): Caption {
    if (dashboardDetail) {
      const filterTakeHomePayAmount = (): string => {
        const takeHomePay = (summary: string[]): boolean => summary[0] === caption.takeHomePay;

        return dashboardDetail.summary.dataRows.filter(takeHomePay)[0][1];
      };

      const stringifyCheckDate = (): string => {
        const checkDate = new Date(dashboardDetail.checkDate);
        const checkDay = checkDate.getDate();
        const stringifiedCheckDate = checkDate.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        });

        const suffix = caption.suffixes[caption.ordinalRules.select(checkDay)];

        return `${stringifiedCheckDate}${suffix}`;
      };

      return {
        amount: filterTakeHomePayAmount(),
        date: stringifyCheckDate(),
        icon: icons[this.randomNumToThree],
        prefix: caption.phrases[this.randomNumToThree],
      };
    } else return null;
  }

  private buildBarChartOptions(paystubDetail?: PaystubData): Highcharts.Options {
    if (paystubDetail) {
      const summary = paystubDetail?.paystubSummaryCollection;
      if (!summary) return;

      //* IN REVERSE ORDER
      const barData = [
        {
          name: 'Post Tax Deductions',
          data: [summary.postTaxDeductionsTotal],
          borderRadiusTopLeft: '50%',
          borderRadiusTopRight: '50%',
        },
        {
          name: 'Taxes',
          data: [summary.taxesTotal],
        },
        {
          name: 'Pre Tax Deductions',
          data: [summary.preTaxDeductionsTotal],
        },
        {
          name: 'Take Home Pay',
          data: [summary.netPay],
          borderRadiusBottomLeft: '50%',
          borderRadiusBottomRight: '50%',
        },
      ];

      return Object.assign({}, barchartOptions, { series: barData });
    } else return null;
  }

  private buildBenefitsDetail(paystubDetail?: PaystubData): BenefitsDetail[] {
    //if paystub exists pull benefits
    if (paystubDetail) {
      const benefits = paystubDetail.paystubDetailCollection.benefitsDetail;
      //return benefits if paystb has them
      return benefits
        ? benefits
            .filter(utils.noHolidayBenefit)
            .sort((a, b) => (a.benefitName > b.benefitName ? -1 : 1))
        : [];
    }
    return [];
  }

  private buildPaystubInfos(checks: Check[]): PaystubInfo[] {
    if (checks.length > 0) {
      /** This is latest from descending order from Service */
      const latestCheckDate = checks[0].checkDate;

      const selectedInfos = checks
        .filter(check => check.checkDate === latestCheckDate)
        .map(utils.toPaystubInfo);

      this.paystubDetailsService.getPaystubDetail(selectedInfos[0]);

      return selectedInfos;
    } else {
      this.paystubDetailsService.getPaystubDetail(null);
      return [];
    }
  }

  private buildPieChartOptions(paystubDetail?: PaystubData): Highcharts.Options {
    if (paystubDetail) {
      const summary = paystubDetail?.paystubSummaryCollection;
      if (!summary) return;

      const data = [
        { name: 'Take Home Pay', y: summary.netPay },
        { name: 'Post Tax Deductions', y: summary.postTaxDeductionsTotal },
        { name: 'Pre Tax Deductions', y: summary.preTaxDeductionsTotal },
        { name: 'Taxes', y: summary.taxesTotal },
      ];
      //* Chart Options
      const series = [{ name: '', type: 'pie', innerSize: '50%', data }];
      return Object.assign({}, piechartOptions, { series });
    } else return null;
  }

  private buildDashboardDetail(paystubDetail?: PaystubData): DashboardDetail {
    if (paystubDetail) {
      const summary = paystubDetail?.paystubSummaryCollection;
      if (!summary) return null;

      const buildDetailRows = (paystubData: PaystubData): DetailRows => {
        const detailRows: DetailRows = {
          paymentRows: [],
          summaryRows: [],
          preTaxRows: [],
          taxRows: [],
          postTaxRows: [],
          earningRows: [],
          netPayRows: [],
          timeOffRows: [],
          rightTables: [],
        };

        const summary = paystubData.paystubSummaryCollection;

        detailRows.summaryRows.push([
          'Take Home Pay',
          formatCurrency(summary.netPay, 'en-us', '$', '1.2-2'),
        ]);

        detailRows.summaryRows.push([
          'Pre Tax Deductions',
          formatCurrency(summary.preTaxDeductionsTotal, 'en-us', '$', '1.2-2'),
        ]);

        detailRows.summaryRows.push([
          'Taxes',
          formatCurrency(summary.taxesTotal, 'en-us', '$', '1.2-2'),
        ]);
        detailRows.summaryRows.push([
          'Post Tax Deductions',
          formatCurrency(summary.postTaxDeductionsTotal, 'en-us', '$', '1.2-2'),
        ]);

        detailRows.netPayRows.push([
          'Net Pay',
          formatCurrency(summary.netPay, 'en-us', '$', '1.2-2'),
          formatCurrency(summary.ytdNetPay, 'en-us', '$', '1.2-2'),
        ]);

        const earningItems =
          paystubData.paystubDetailCollection.earningsDetail === null
            ? []
            : paystubData.paystubDetailCollection.earningsDetail;

        const timeOffLines = earningItems.filter(
          (earning: { timeOffHours: number }) => earning.timeOffHours !== 0
        );

        const earningLines = earningItems.filter(
          (earning: { hour: number; timeOffHours: number }) =>
            earning.hour !== 0 && earning.timeOffHours === 0
        );

        if (earningLines.length > 0) {
          detailRows.rightTables.push({
            title: 'Hours Worked',
            headerCells: ['Type', 'Hours'],
            dataRows: detailRows.earningRows,
          });
          earningLines.forEach((earningItem: PaystubDetailItem) => {
            detailRows.earningRows.push([
              earningItem.itemName,
              formatCurrency(earningItem.hour, 'en-us', '', '1.2-2'),
            ]);
          });
        }

        if (timeOffLines.length > 0) {
          detailRows.rightTables.push({
            title: 'Time Off',
            headerCells: ['Type', 'Hours'],
            dataRows: detailRows.timeOffRows,
          });
          timeOffLines.forEach((earningItem: PaystubDetailItem) => {
            detailRows.timeOffRows.push([
              earningItem.itemName,
              formatCurrency(earningItem.timeOffHours, 'en-us', '', '1.2-2'),
            ]);
          });
        }

        return detailRows;
      };

      const paystubRows: DetailRows = buildDetailRows(paystubDetail);
      const detailRows = paystubRows;

      return {
        checkDate: formatDate(summary.checkDate, 'longDate', 'en-US'),
        nextPayDay: formatDate(summary.nextPayDay, 'shortDate', 'en-US'),
        periodStartDate: formatDate(summary.periodBeginDate, 'shortDate', 'en-US'),
        periodEndDate: formatDate(summary.periodEndDate, 'shortDate', 'en-US'),
        adjustmentType: summary.adjustmentType,
        summary: {
          color: '',
          icon: '',
          title: 'Summary',
          headerCells: ['', ''],
          dataRows: detailRows.summaryRows,
          colspan: 2,
        },

        rightTableCollection: detailRows.rightTables,
      };
    } else {
      return null;
    }
  }

  private buildNoTaxWidgetsIfMobile(widgets: WidgetDetail[], isMobile: boolean) {
    const isNotTaxWidget = (widget: WidgetDetail) => !mobileTaxWidgets.includes(widget.type);

    return isMobile ? widgets.filter(isNotTaxWidget) : widgets;
  }

  private buildTaxOnlyWidget(widgets: WidgetDetail[]) {
    const isTaxWidget = (widget: WidgetDetail) => mobileTaxWidgets.includes(widget.type);

    return widgets.filter(isTaxWidget);
  }

  private buildWidgetList(
    profile: EmployeePackage,
    taxFormsNotice: TaxForm,
    paystubData: PaystubData
  ): WidgetDetail[] {
    //#region //? Getters
    const benefits = paystubData ? this.buildBenefitsDetail(paystubData) : [];

    //#region //* Paperless Getters
    const isPaperCheck = profile.bankAccounts.length === 0;
    //#endregion

    //#region //* Time Off Getters
    const is1099Contractor = profile.personal.is1099Contractor;

    const is1099OrNoBenefits = is1099Contractor || benefits === null || benefits?.length === 0;
    //#endregion

    //#region //* Tax Forms Getters
    const { fileId, fileYear, fileType } = taxFormsNotice;
    //create flag to differentiate W2 or 1099 notice
    const fileTypeW2 = fileType === TaxFormType.EmployeeW2;
    //noticeYear will be used in the description of the notice (it is always next year from year in the taxFormsNotice)
    const noticeYear = `${fileYear + 1}`;
    //#endregion
    //#endregion

    //#region //? Build Time Off Widget OR Facts Widget, if no Benefits.
    const buildTimeOffOrFactsWidget = (): WidgetDetail => {
      //* Create Time Off Widget Content For Time Off Table
      const createTimeOffWidgetContent = (benefits?: BenefitsDetail[]): WidgetContent[] => {
        if (!is1099OrNoBenefits) {
          return benefits?.map(benefit => {
            return {
              name: benefit?.benefitName,
              benefitsBalance: benefit?.benefitsBalance.toFixed(1),
              maximumBalance: utils.buildMaxBalance(benefit?.maximumBalance),
            };
          });
        }
      };

      //* Create Facts Widget Config
      const factsWidgetConfig: WidgetDetail = {
        type: WidgetType.Facts,
        showImage: ImagePosition.Hidden,
        randomFact: widgetFacts[this.randomNumToThree],
      };

      //* Create Time Off Widget Config
      const timeOffWidgetConfig: WidgetDetail = {
        type: WidgetType.TimeOff,
        showImage: ImagePosition.Hidden,
        content: createTimeOffWidgetContent(benefits),
      };
      //* Build Widget
      return is1099OrNoBenefits ? buildWidget(factsWidgetConfig) : buildWidget(timeOffWidgetConfig);
    };
    //#endregion

    //#region //? Build Tax Forms Widget
    const buildTaxFormsWidget = (): WidgetDetail => {
      //* 2 types of widget notices generated in the dashboard
      //* define W2 or 1099 form version using flag
      const formWidgetSoon = fileTypeW2 ? WidgetType.FormW2Soon : WidgetType.Form1099Soon;
      const formWidgetReady = fileTypeW2 ? WidgetType.FormW2Ready : WidgetType.Form1099Ready;

      //* if fileType passed through the service and fileId more than 0
      //* show formWidgetReady, otherwise formWidgetSoon
      const widgetTaxType = (): WidgetType => {
        if (fileType) return fileId ? formWidgetReady : formWidgetSoon;
      };

      //* Create Tax Widget Config
      const taxWidgetConfig: WidgetDetail = {
        type: widgetTaxType(),
        showImage: ImagePosition.Hidden,
        year: noticeYear,
      };

      //* Build Widget
      return buildWidget(taxWidgetConfig);
    };
    //#endregion

    //#region //? Build Paperless Widget
    const buildPaperlessWidget = (): WidgetDetail => {
      //* Create Paperless Widget Config
      const paperlessWidgetConfig: WidgetDetail = { type: WidgetType.Paperless };

      return isPaperCheck && buildWidget(paperlessWidgetConfig);
      //* if user has 1 paystub && has a papercheck, SHOW "go paperless" widget
    };
    //#endregion

    //#region //? Build List of Finished Widgets
    //* add images to widgets
    const addImagesToWidgets = (widgets: WidgetDetail[]): WidgetDetail[] => {
      //* control if widget shows an image.
      const showImage = (widget: WidgetDetail, show: ImagePosition) => ({
        ...widget,
        showImage: show,
      });

      //* change value of showImage on the widget control.
      const widgetsWithImage = (show: ImagePosition) =>
        widgets.map(widget => showImage(widget, show));
      const timeOffWidget = widgets.filter(widget => widget.type === WidgetType.TimeOff);
      //* if timeoff widget is single on the page -> show image otherwise no image for timeoff widget
      //*  total visible widgets on the page -> filteredEmptyWidgets.length
      if (widgets.length === 1) return widgetsWithImage(ImagePosition.Bottom);
      if (widgets.length === 2 && timeOffWidget) {
        const assignImagesToWidgets = widgetsWithImage(ImagePosition.Middle);
        const exceptions = assignImagesToWidgets.findIndex(
          widget => widget.type === WidgetType.TimeOff
        );

        //* remove image from TimeOff widget if any other widget shows on the page
        if (exceptions > 0) {
          assignImagesToWidgets[exceptions].showImage = ImagePosition.Hidden;
        }
        return assignImagesToWidgets;
      } else {
        //* 3 is the max amount of widgets for it's grid container.
        return widgets.length === 3
          ? widgetsWithImage(ImagePosition.Hidden)
          : widgetsWithImage(ImagePosition.Middle);
      }
    };

    const buildWidgets = (widgets: WidgetDetail[]) => {
      //* filter out empty widgets.
      const filteredEmptyWidgets = widgets.filter(widget => widget.type !== WidgetType.None);
      //* add images to widgets and return the result to build widgets
      return addImagesToWidgets(filteredEmptyWidgets);
    };

    //* Create list of created widgets.
    const widgets = [
      buildTaxFormsWidget(),
      //buildPaperlessWidget(),
      buildTimeOffOrFactsWidget(),
    ];

    //* Filter & Toggle Image Control for Widget
    //* Return list of finished widgets.
    return buildWidgets(widgets);
    //#endregion
  }
}
